import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as S from './CookieBanner.style';
import {
  loadConsent,
  saveConsent,
  COOKIE_CONSENT_KEY,
  COOKIE_CONSENT_VERSION,
} from 'Utils/cookieConsent';

const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [statistics, setStatistics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    setVisible(!loadConsent());

    // Allow other components (e.g. footer link) to re-open the banner programmatically.
    const onOpen = () => {
      const current = loadConsent();
      if (current) {
        setStatistics(current.statistics);
        setMarketing(current.marketing);
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener('mrlw:openCookieBanner', onOpen);

    // Sync if consent changes elsewhere (e.g. clearConsent)
    const onConsent = (e: any) => {
      if (!e?.detail) setVisible(true);
    };
    window.addEventListener('mrlw:cookieConsent', onConsent as any);

    return () => {
      window.removeEventListener('mrlw:openCookieBanner', onOpen);
      window.removeEventListener('mrlw:cookieConsent', onConsent as any);
    };
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent(true, true);
    setVisible(false);
  };

  const rejectAll = () => {
    saveConsent(false, false);
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent(statistics, marketing);
    setVisible(false);
  };

  return (
    <S.Wrapper role="dialog" aria-modal="false" aria-labelledby="cookie-banner-title" data-testid="cookie-banner">
      <S.Card>
        <S.Title id="cookie-banner-title">{t('cookies.title')}</S.Title>
        <S.Text>
          {t('cookies.intro')}{' '}
          <Link to="/datenschutz" data-testid="cookie-banner-privacy-link">
            {t('cookies.privacy_link')}
          </Link>
        </S.Text>

        {showDetails && (
          <S.Categories data-testid="cookie-banner-details">
            <S.Category $disabled>
              <span>
                <strong>{t('cookies.cat_necessary_title')}</strong>
                <small>{t('cookies.cat_necessary_desc')}</small>
              </span>
              <S.Toggle checked readOnly disabled />
            </S.Category>

            <S.Category>
              <span>
                <strong>{t('cookies.cat_statistics_title')}</strong>
                <small>{t('cookies.cat_statistics_desc')}</small>
              </span>
              <S.Toggle
                checked={statistics}
                onChange={(e) => setStatistics(e.target.checked)}
                data-testid="cookie-toggle-statistics"
              />
            </S.Category>

            <S.Category>
              <span>
                <strong>{t('cookies.cat_marketing_title')}</strong>
                <small>{t('cookies.cat_marketing_desc')}</small>
              </span>
              <S.Toggle
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                data-testid="cookie-toggle-marketing"
              />
            </S.Category>
          </S.Categories>
        )}

        <S.Actions>
          <S.SecondaryBtn onClick={rejectAll} data-testid="cookie-reject-all">
            {t('cookies.reject_all')}
          </S.SecondaryBtn>
          {showDetails ? (
            <S.PrimaryBtn onClick={saveCustom} data-testid="cookie-save">
              {t('cookies.save_selection')}
            </S.PrimaryBtn>
          ) : (
            <S.PrimaryBtn onClick={acceptAll} data-testid="cookie-accept-all">
              {t('cookies.accept_all')}
            </S.PrimaryBtn>
          )}
        </S.Actions>

        {!showDetails ? (
          <S.LinkBtn onClick={() => setShowDetails(true)} data-testid="cookie-show-details">
            {t('cookies.show_settings')}
          </S.LinkBtn>
        ) : (
          <S.LinkBtn onClick={acceptAll} data-testid="cookie-accept-from-details">
            {t('cookies.accept_all')}
          </S.LinkBtn>
        )}

        {/* Hidden marker for testing: the key under which consent is persisted */}
        <input type="hidden" value={COOKIE_CONSENT_KEY} readOnly data-testid="cookie-storage-key" />
        <input type="hidden" value={String(COOKIE_CONSENT_VERSION)} readOnly data-testid="cookie-storage-version" />
      </S.Card>
    </S.Wrapper>
  );
};

export default CookieBanner;
